import { COMMON_CONSTANTS, getTranslatedText } from '@nile/components';
import {
  MAC_POLICY_SET_ID,
  WINDOWS_POLICY_SET_ID,
  LINUX_POLICY_SET_ID,
  PRINTER_ID,
  FIREWALL_ID,
  ROUTER_ID,
  SWITCH_ID
} from '@nile/banyan/src/Constants/policySet';
import { isEmpty } from 'lodash';
import TABS from '@nile/banyan/src/Pages/Constants/policyTabs';
import { imageMap } from '@nile/rmm/src/modules/asset-management/constants/asset-details';
import {
  SUPPORTED_MODULE_TYPES,
  POLICY_MANAGEMENT_TYPE
} from '@nile/rmm/src/constants';

const getOSNameBasedOnClassId = (classId, intl) => {
  switch (classId) {
    case '1':
      return getTranslatedText(intl, 'admin.policyManagement.windows');
    case '2':
      return getTranslatedText(intl, 'admin.policyManagement.linux');
    case '3':
      return getTranslatedText(intl, 'admin.policyManagement.mac');
    default:
      break;
  }
};
const getAuthorisedTabs = (tabsList, userRole, isAuthorised) => {
  return tabsList.filter((tb) => {
    if (!tb.permission) {
      return true;
    }
    return isAuthorised(userRole, tb.permission);
  });
};
const getTabs = (config, intl) => {
  const {
    classId,
    category,
    intelligentAlertingFlag,
    source,
    acronisFlag,
    flags,
    userRole,
    isAuthorised = null,
    supportedModules,
    currentProduct,
    rmmBetaFeature = {}
  } = config;

  let processedTabs = isAuthorised
    ? getAuthorisedTabs(TABS(flags, intl), userRole, isAuthorised)
    : TABS(flags, intl);

  if (supportedModules?.includes(SUPPORTED_MODULE_TYPES.NETWORK_MONITORING)) {
    const isAssetGroup = category === 'ASSET_GROUP';
    const fromAssetDetail = source === 'ASSET_DETAILS';

    const tabNames =
      isAssetGroup && !fromAssetDetail
        ? ['alertManagement', 'assetGroup', 'mainatenance']
        : ['alertManagement', 'mainatenance'];

    processedTabs = processedTabs.filter(
      (t) => tabNames.indexOf(t.name) !== -1
    );
    return processedTabs;
  }

  if (!intelligentAlertingFlag) {
    processedTabs = processedTabs.filter((el) => {
      return el.name !== 'intelligentAlerting';
    });
  }

  if (!acronisFlag) {
    processedTabs = processedTabs.filter((el) => {
      return el.name !== 'dataBackup';
    });
  }

  if (
    category === 'HIERARCHICAL' ||
    category === 'ASSET_CLASS' ||
    category === 'ADVANCED_HIERARCHY'
  ) {
    processedTabs = processedTabs.filter((el) => {
      return el.name !== 'assetGroup';
    });
  }

  if (source === 'ASSET_DETAILS' && category === 'ASSET_GROUP') {
    processedTabs = processedTabs.filter((el) => {
      return el.name !== 'assetGroup';
    });
  }

  if (classId === MAC_POLICY_SET_ID || classId === LINUX_POLICY_SET_ID) {
    // const hiddenTabList = ['softwareManagement', 'patchManagement'];
    // Below condition this is only for launch darkly flag
    var hiddenTabList = [];
    if (flags?.macPatch) {
      hiddenTabList = ['softwareManagement'];
      // Beta badge added only for mac patch
      // Beta badge removed
      // processedTabs = processedTabs.map((el) =>
      //   el.name === 'patchManagement'
      //     ? {
      //         ...el,
      //         isNewFeature: true,
      //         badgeText: getTranslatedText(intl, 'common.beta')
      //       }
      //     : el
      // );
    } else {
      hiddenTabList = ['softwareManagement', 'patchManagement'];
    }
    if (classId === LINUX_POLICY_SET_ID) {
      hiddenTabList = [
        'softwareManagement',
        'antivirus',
        'dataBackup',
        'systemTray'
      ];
      if (
        !rmmBetaFeature?.linuxOsPatching ||
        category === POLICY_MANAGEMENT_TYPE?.ASSET_GROUP
      ) {
        hiddenTabList.push('patchManagement');
      }
    }
    // if custom software flag there enable softwareManagement -> mac/linux
    hiddenTabList = hiddenTabList.filter((v) =>
      rmmBetaFeature?.agentCustomSoftware &&
      flags?.homebrew &&
      v == 'softwareManagement'
        ? false
        : true
    );
    if (classId === PRINTER_ID) {
      hiddenTabList = ['intelligentAlerting', 'patchManagement'];
    }

    processedTabs = processedTabs.filter((el) => {
      return !hiddenTabList.includes(el.name);
    });
  }

  // Hide software management tab based on feature flag
  if (flags && classId === WINDOWS_POLICY_SET_ID) {
    processedTabs = processedTabs.filter((el) => {
      if (flags.hasOwnProperty(el.flag)) {
        return flags[el.flag];
      }
      return true;
    });
  }
  /** TEMPORARY FIX, REMOVE THIS CODE ONCE ANTIVIRUS & Databack  integrations are supported for IT product */
  if (currentProduct === COMMON_CONSTANTS.IT) {
    processedTabs = processedTabs.filter((el) => {
      if (el.productToCheck) {
        return el.productToCheck === currentProduct;
      }
      return true;
    });
  }

  return processedTabs;
};

const getInitialActiveTab = (config) => {
  const {
    intelligentAlertingFlag,
    tabContext,
    processedTabs,
    tabToHighlightQP
  } = config;
  const preservedState = tabContext?.activeTab?.state;

  if (preservedState?.alertPolicyDetails && intelligentAlertingFlag) {
    return processedTabs[1] ? processedTabs[1] : processedTabs[0];
  }
  // this case is required when we create a new policy set(group based policy), we navigate to the edit policy set by highlighting the tab which was active in create policy set
  if (!isEmpty(tabToHighlightQP)) {
    let params = new URLSearchParams(tabToHighlightQP);
    return processedTabs.find((el) => {
      return el.name === params.get('activeTab');
    });
  }
  return processedTabs[0];
};

const alertPolicyTypeMap = {
  hardwarePolicyType: 'Hardware Performance Monitoring',
  processPolicyType: 'Processes/Services Monitoring',
  eventLogPolicyType: 'Eventlog monitoring',
  generalPolicyType: 'General Monitoring',
  intelligentAlertingPolicyType: 'Intelligent Alerting',
  softwareManagement: 'Software management',
  networkMonitorPolicyType: 'Network Monitoring',
  patchPolicyType: 'Patch Management',
  patchAlertPolicyType: 'Patch Monitoring',
  remotePolicyType: 'Remote Desktop',
  maintenanceType: 'Scheduled Maintenance',
  agentPolicyType: 'Agent Settings',
  assetOnboardingPolicyType: 'Onboarding Management',
  softwareMonitoringPolicyType: 'Software Monitoring'
};
const getPolicyCategory = (policyType) => {
  switch (policyType) {
    case 'HIERARCHICAL':
      return 'ASSET_CLASS';
    case 'ASSET_GROUP':
      return 'ASSET_GROUP';
    case 'ADVANCED_HIERARCHY':
      return 'ADVANCED_HIERARCHY';
    default:
      return null;
  }
};
const dataBackupPolicyType = 'Backup Monitoring';

const matchesFilter = (displayFilter, supportedDeviceTypes = []) => {
  if (
    !displayFilter ||
    displayFilter === 'ALL' ||
    supportedDeviceTypes?.includes('ALL')
  )
    return true;
  return supportedDeviceTypes?.includes(displayFilter);
};

const builtCollapsibleTabs = ({
  mdmMeta,
  displayFilter,
  checkShowInAssetPage = false
}) => {
  const rootSections =
    mdmMeta?.sections && Array.isArray(mdmMeta?.sections)
      ? mdmMeta.sections
      : [];

  const builtSections = [];
  rootSections.forEach((section) => {
    if (!matchesFilter(displayFilter, section?.supportedDeviceTypes)) return;

    // Skip sections with showInAssetPage = false if checking
    if (checkShowInAssetPage && section?.showInAssetPage === false) return;

    const childSections = Array.isArray(section?.sections)
      ? section.sections
      : [];

    // Filter child sections
    const filteredChildren = childSections
      .filter((child) => {
        // Skip dividers (always excluded from sections in new component)
        if (child?.isDivider) return false;
        // Skip if showSection is false
        if (
          child?.showSection === false ||
          (checkShowInAssetPage && child?.showInAssetPage === false)
        )
          return false;
        // Apply device type filter
        return matchesFilter(displayFilter, child?.supportedDeviceTypes);
      })
      .map((child) => ({
        id: child?.id,
        name: child?.id || child?.policyType || child?.header,
        label: child?.header || child?.defaultLabel,
        defaultLabel: child?.defaultLabel || child?.header,
        iconName: child?.iconName,
        isSuperIcon: true,
        policyType: child?.policyType,
        childSections: child?.sections || [],
        supportedDeviceTypes: child?.supportedDeviceTypes || [],
        isBeta: child?.isBeta || false,
        isNewFeature: child?.isNewFeature || false,
        mdmPolicy: child?.mdmPolicy || false,
        showInTabs: child?.showInTabs || false,
        isGated: child?.isGated || false
      }));

    // Only add section if it has children
    if (filteredChildren.length > 0) {
      builtSections.push({
        id: section?.id,
        name: section?.id || section?.policyType || section?.header,
        label: section?.defaultLabel || section?.header,
        defaultLabel: section?.defaultLabel || section?.header,
        iconName: section?.iconName,
        isSuperIcon: !!section?.iconName,
        policyType: section?.policyType,
        sections: filteredChildren,
        supportedDeviceTypes: section?.supportedDeviceTypes || [],
        mdmPolicy: section?.mdmPolicy || false,
        showInTabs: section?.showInTabs || false,
        isGated: section?.isGated || false
      });
    }
  });
  return builtSections;
};

const builtFlatTabs = ({
  mdmMeta,
  displayFilter,
  checkShowInAssetPage = false
}) => {
  const rootSections =
    mdmMeta?.sections && Array.isArray(mdmMeta?.sections)
      ? mdmMeta.sections
      : [];

  const builtTabs = [];
  rootSections.forEach((section) => {
    if (!matchesFilter(displayFilter, section?.supportedDeviceTypes)) return;

    // Skip sections with showInAssetPage = false if checking
    if (checkShowInAssetPage && section?.showInAssetPage === false) return;

    // Check if section is clickable
    const isClickable = section?.clickable === 'true';

    if (isClickable) {
      // If section is clickable, render it as a regular tab
      builtTabs.push({
        name: section?.id || section?.policyType || section?.header,
        label: section?.defaultLabel || section?.header,
        icon: section?.iconName,
        isSuperIcon: !!section?.iconName,
        isClickableHeader: true,
        policyType: section?.policyType,
        id: section?.id,
        childSections: section?.sections || [],
        supportedDeviceTypes: section?.supportedDeviceTypes || []
      });
    } else {
      // If not clickable, render as section header
      builtTabs.push({
        name: section?.policyType || section?.header,
        label: section?.defaultLabel || section?.header,
        isSectionHeader: true,
        policyType: section?.policyType,
        id: section?.id,
        childSections: section?.sections || [],
        supportedDeviceTypes: section?.supportedDeviceTypes || []
      });
    }

    const childSections = Array.isArray(section?.sections)
      ? section.sections
      : [];

    // Handle child sections and dividers from JSON
    childSections.forEach((child) => {
      // Handle divider objects from JSON (always include, no filter needed)
      if (child?.isDivider) {
        builtTabs.push({
          name: child?.id,
          isDivider: true
        });
        return;
      }

      if (!matchesFilter(displayFilter, child?.supportedDeviceTypes)) return;
      if (
        child?.showSection === false ||
        (checkShowInAssetPage && child?.showInAssetPage === false)
      )
        return;

      builtTabs.push({
        name: child?.id || child?.policyType || child?.header,
        label: child?.header || child?.defaultLabel,
        icon: child?.iconName,
        isSuperIcon: true,
        policyType: child?.policyType,
        id: child?.id,
        childSections: child?.sections || [],
        supportedDeviceTypes: child?.supportedDeviceTypes || []
      });
    });
  });
  return builtTabs;
};

const createAssetGroupObject = (intl) => {
  return {
    id: 'assetGroup',
    name: 'assetGroup',
    label: getTranslatedText(intl, 'admin.policyManagement.assetGroup.tabs'),
    defaultLabel: getTranslatedText(
      intl,
      'admin.policyManagement.assetGroup.tabs'
    ),
    iconName: 'AssetGroup',
    isSuperIcon: true,
    policyType: null,
    childSections: [],
    supportedDeviceTypes: ['ALL'],
    isBeta: false,
    isNewFeature: false,
    mdmPolicy: false,
    isGated: false
  };
};

const addAssetGroupToRmmConfigurations = (builtSections, intl) => {
  const rmmConfigSection = builtSections.find(
    (section) => section.id === 'rmm_configurations'
  );

  if (rmmConfigSection) {
    const assetGroupExists = rmmConfigSection.sections?.some(
      (child) => child.id === 'assetGroup'
    );

    if (!assetGroupExists) {
      const assetGroupObject = createAssetGroupObject(intl);
      rmmConfigSection.sections = [
        ...(rmmConfigSection.sections || []),
        assetGroupObject
      ];
    }
  }
};

export {
  getOSNameBasedOnClassId,
  getTabs,
  imageMap,
  alertPolicyTypeMap,
  getInitialActiveTab,
  dataBackupPolicyType,
  getPolicyCategory,
  builtCollapsibleTabs,
  builtFlatTabs,
  createAssetGroupObject,
  addAssetGroupToRmmConfigurations
};
